// src/services/CompatibilityLayer.js
// This file provides compatibility for missing service functions
//
// Add this file to your project and import it in your App.js
// Call initializeCompatibilityLayer() at app startup to ensure compatibility
//
// Usage: 
// import { initializeCompatibilityLayer } from './services/CompatibilityLayer';
// initializeCompatibilityLayer();

import DocumentService from './DocumentService';
import AutoCacheService from './AutoCacheService';
import BandwidthLimitedDownloadService from './BandwidthLimitedDownloadService';
import { storage } from '../firebase';
import { ref, getMetadata, getDownloadURL } from 'firebase/storage';
import { openDB } from 'idb'; // Importing openDB for IndexedDB access

/**
 * This function should be called when the app starts
 * It ensures all required functions are available on the services
 */
const initializeCompatibilityLayer = () => {
  console.log('[Compatibility] Initializing service compatibility layer');
  
  // Add missing functions to DocumentService
  addMissingFunctionsToDocumentService();
  
  // Patch AutoCacheService to be more resilient
  patchAutoCacheService();
  
  // Patch BandwidthLimitedDownloadService
  patchBandwidthLimitedDownloadService();
  
  // Add enhanced offline detection
  enhanceOfflineDetection();
  
  console.log('[Compatibility] Service compatibility layer initialized');
};

/**
 * Add enhanced offline detection to the window object
 */
const enhanceOfflineDetection = () => {
  window.networkStatus = {
    isActuallyOnline: true,
    lastChecked: Date.now()
  };
  
  // Function to actually check network connectivity
  window.checkNetworkConnectivity = async () => {
    if (!navigator.onLine) {
      window.networkStatus.isActuallyOnline = false;
      window.networkStatus.lastChecked = Date.now();
      return false;
    }
    
    try {
      // Use a quick HEAD request with timeout to check real connectivity
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      // Cloudflare's DNS service is reliable and responds to HEAD requests
      const response = await fetch('https://1.1.1.1', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      window.networkStatus.isActuallyOnline = true;
      window.networkStatus.lastChecked = Date.now();
      return true;
    } catch (error) {
      console.log('[Network Check] Failed, we are actually offline:', error);
      window.networkStatus.isActuallyOnline = false;
      window.networkStatus.lastChecked = Date.now();
      return false;
    }
  };
  
  // Add event listeners for online/offline events
  window.addEventListener('online', () => {
    // Don't immediately trust the online event - verify with an actual network request
    window.checkNetworkConnectivity().then(isOnline => {
      console.log(`[Network Status] Online event triggered, actual status: ${isOnline ? 'online' : 'still offline'}`);
    });
  });
  
  window.addEventListener('offline', () => {
    window.networkStatus.isActuallyOnline = false;
    window.networkStatus.lastChecked = Date.now();
    console.log('[Network Status] Offline event triggered');
  });
};

/**
 * Add missing functions to DocumentService
 * This provides compatibility for functions that might be called
 * by other services but don't exist in your implementation
 */
const addMissingFunctionsToDocumentService = () => {
  // Function: saveDocumentsToIndexedDB
  if (!DocumentService.saveDocumentsToIndexedDB) {
    console.log('[Compatibility] Adding saveDocumentsToIndexedDB to DocumentService');
    
    DocumentService.saveDocumentsToIndexedDB = async (documents, sectionPath) => {
      console.log("[Compatibility] Called saveDocumentsToIndexedDB", { documents, sectionPath });
      
      try {
        // Open the database
        const db = await openDB('trinityHarperDocs', 1, {
          upgrade(db) {
            // Create stores if they don't exist
            if (!db.objectStoreNames.contains('documents')) {
              const docStore = db.createObjectStore('documents', { keyPath: 'id' });
              docStore.createIndex('section', 'section', { unique: false });
            }
            if (!db.objectStoreNames.contains('syncStatus')) {
              db.createObjectStore('syncStatus', { keyPath: 'section' });
            }
            if (!db.objectStoreNames.contains('documentContents')) {
              db.createObjectStore('documentContents', { keyPath: 'id' });
            }
          }
        });
        
        // Add all documents in a transaction
        const tx = db.transaction('documents', 'readwrite');
        const store = tx.objectStore('documents');
        
        for (const doc of documents) {
          await store.put(doc);
          console.log(`[Compatibility] Saved document ${doc.name} to IndexedDB`);
        }
        
        // Update sync status
        const syncTx = db.transaction('syncStatus', 'readwrite');
        const syncStore = syncTx.objectStore('syncStatus');
        await syncStore.put({
          section: sectionPath,
          lastSync: new Date().toISOString()
        });
        
        await tx.done;
        await syncTx.done;
        
        return true;
      } catch (error) {
        console.error('[Compatibility] Error in saveDocumentsToIndexedDB:', error);
        return false;
      }
    };
  }
  
  // Function: getDocumentMetadata
  if (!DocumentService.getDocumentMetadata) {
    console.log('[Compatibility] Adding getDocumentMetadata to DocumentService');
    
    DocumentService.getDocumentMetadata = async (documentId) => {
      console.log("[Compatibility] Called getDocumentMetadata for", documentId);
      
      try {
        // First attempt: Try to get the document directly from IndexedDB
        try {
          const db = await openDB('trinityHarperDocs', 1);
          const tx = db.transaction('documents', 'readonly');
          const store = tx.objectStore('documents');
          const doc = await store.get(documentId);
          
          if (doc) {
            console.log(`[Compatibility] Found document in IndexedDB: ${documentId}`);
            return doc;
          }
        } catch (dbError) {
          console.warn(`[Compatibility] IndexedDB lookup failed: ${dbError.message}`);
        }
        
        // Second attempt: Try to reconstruct path from documentId more intelligently
        
        // Replace Firebase_upload_ with Firebase_upload/
        let path = documentId.replace('Firebase_upload_', 'Firebase_upload/');
        
        // Identify major sections in the path
        const majorSections = [
          'Master Vehicle File',
          'Health & Safety Policy Manual',
          'Insurance Certificates',
          'COSHH',
          'Risk Assessments',
          'Safe Systems of Work',
          'Employee Handbook'
        ];
        
        // Replace section names
        majorSections.forEach(section => {
          const sectionWithUnderscores = section.replace(/\s+/g, '_');
          const regex = new RegExp(`_${sectionWithUnderscores}`, 'g');
          path = path.replace(regex, `/${section}`);
        });
        
        // Handle numerals before section names (e.g., "1_" -> "1. ")
        path = path.replace(/\/(\d+)_\s+/g, '/$1. ');
        
        // Replace file extension marker
        path = path.replace(/_([a-zA-Z0-9]{1,5})$/, '.$1');
        
        console.log(`[Compatibility] Reconstructed path: ${path}`);
        
        // Return a basic metadata object
        return {
          id: documentId,
          path: path,
          name: path.split('/').pop() || documentId
        };
      } catch (error) {
        console.error('[Compatibility] Error in getDocumentMetadata:', error);
        throw error;
      }
    };
  }
  
  // Add path reconstruction utility if it doesn't exist
  if (!DocumentService.getPathFromDocumentId) {
    console.log('[Compatibility] Adding getPathFromDocumentId to DocumentService');
    
    DocumentService.getPathFromDocumentId = (documentId) => {
      if (!documentId) return null;
      
      // Replace Firebase_upload_ with Firebase_upload/
      let path = documentId.replace('Firebase_upload_', 'Firebase_upload/');
      
      // Handle major sections
      const majorSections = [
        'Master Vehicle File',
        'Health & Safety Policy Manual',
        'Insurance Certificates',
        'COSHH',
        'Risk Assessments',
        'Safe Systems of Work',
        'Employee Handbook'
      ];
      
      majorSections.forEach(section => {
        const sectionWithUnderscores = section.replace(/\s+/g, '_');
        const regex = new RegExp(`_${sectionWithUnderscores}`, 'g');
        path = path.replace(regex, `/${section}`);
      });
      
      // Fix numeric prefixes (e.g., "1_" -> "1. ")
      path = path.replace(/\/(\d+)_\s+/g, '/$1. ');
      
      // Fix file extension
      path = path.replace(/_([a-zA-Z0-9]{1,5})$/, '.$1');
      
      console.log(`[Compatibility] Converted ID to path: ${path}`);
      return path;
    };
  }
  
  // Add storeBlobInIndexedDB if it doesn't exist
  if (!DocumentService.storeBlobInIndexedDB) {
    console.log('[Compatibility] Adding storeBlobInIndexedDB to DocumentService');
    
    DocumentService.storeBlobInIndexedDB = async (documentId, blob) => {
      try {
        const db = await openDB('trinityHarperDocs', 1, {
          upgrade(db) {
            if (!db.objectStoreNames.contains('documentContents')) {
              db.createObjectStore('documentContents', { keyPath: 'id' });
            }
          }
        });
        const tx = db.transaction('documentContents', 'readwrite');
        const store = tx.objectStore('documentContents');
        await store.put({
          id: documentId,
          content: blob,
          lastCached: new Date().toISOString()
        });
        await tx.done;
        
        console.log(`[Compatibility] Successfully stored blob for document: ${documentId}`);
        return true;
      } catch (error) {
        console.error('[Compatibility] Error storing blob in IndexedDB', error);
        throw error;
      }
    };
  }
  
  // Add updateDocumentCacheStatus if it doesn't exist
  if (!DocumentService.updateDocumentCacheStatus) {
    console.log('[Compatibility] Adding updateDocumentCacheStatus to DocumentService');
    
    DocumentService.updateDocumentCacheStatus = async (documentId) => {
      try {
        const db = await openDB('trinityHarperDocs', 1);
        const docTx = db.transaction('documents', 'readwrite');
        const docStore = docTx.objectStore('documents');
        const doc = await docStore.get(documentId);
        if (doc) {
          doc.lastCached = new Date().toISOString();
          await docStore.put(doc);
          console.log(`[Compatibility] Updated document cache status for ${documentId}`);
        }
        await docTx.done;
        return true;
      } catch (error) {
        console.error('[Compatibility] Error updating document cache status', error);
        throw error;
      }
    };
  }
  
  // Add enhanced isDocumentCached with better error handling
  const originalIsDocumentCached = DocumentService.isDocumentCached;
  if (originalIsDocumentCached) {
    DocumentService.isDocumentCached = async (documentId) => {
      try {
        return await originalIsDocumentCached(documentId);
      } catch (error) {
        console.error(`[Compatibility] Error in isDocumentCached: ${error.message}`);
        // During errors, check if we can directly access the IndexedDB
        try {
          const db = await openDB('trinityHarperDocs', 1);
          const contentTx = db.transaction('documentContents', 'readonly');
          const contentStore = contentTx.objectStore('documentContents');
          const cachedContent = await contentStore.get(documentId);
          
          return !!(cachedContent && cachedContent.content);
        } catch (fallbackError) {
          console.warn(`[Compatibility] Fallback cache check failed: ${fallbackError.message}`);
          return false; // Default to not cached during errors
        }
      }
    };
  } else {
    // Add isDocumentCached if it doesn't exist
    DocumentService.isDocumentCached = async (documentId) => {
      try {
        const db = await openDB('trinityHarperDocs', 1);
        const contentTx = db.transaction('documentContents', 'readonly');
        const contentStore = contentTx.objectStore('documentContents');
        const cachedContent = await contentStore.get(documentId);
        
        if (cachedContent && cachedContent.content) {
          console.log(`[Compatibility] Document is cached: ${documentId}`);
          return true;
        }
        
        console.log(`[Compatibility] Document is not cached: ${documentId}`);
        return false;
      } catch (error) {
        console.error(`[Compatibility] Error checking if document is cached: ${documentId}`, error);
        return false;
      }
    };
  }
};

// Ensure the functions for AutoCacheService exist
const patchAutoCacheService = () => {
  console.log('[Compatibility] Patching AutoCacheService');
  
  // Make sure basic properties exist
  if (!AutoCacheService.isSyncing) {
    AutoCacheService.isSyncing = false;
  }
  
  if (!AutoCacheService.syncedSections) {
    AutoCacheService.syncedSections = new Set();
  }
  
  if (!AutoCacheService.progress) {
    AutoCacheService.progress = {
      total: 0,
      completed: 0,
      sections: {},
    };
  }
  
  // Ensure the initialize method exists with better error handling
  if (AutoCacheService.initialize) {
    const originalInitialize = AutoCacheService.initialize;
    
    AutoCacheService.initialize = async () => {
      // Verify network status before trying to sync
      let isActuallyOnline = navigator.onLine;
      
      if (isActuallyOnline && window.checkNetworkConnectivity) {
        isActuallyOnline = await window.checkNetworkConnectivity();
      }
      
      if (!isActuallyOnline) {
        console.log('[AutoCacheService] Device is offline, cannot sync');
        return false;
      }
      
      try {
        return await originalInitialize();
      } catch (error) {
        console.error('[AutoCacheService] Error during initialization:', error);
        AutoCacheService.isSyncing = false;
        return false;
      }
    };
  } else {
    // Create a basic implementation if missing
    AutoCacheService.initialize = async () => {
      console.log('[AutoCacheService] Stub initialize called');
      if (!navigator.onLine) {
        return false;
      }
      
      AutoCacheService.isSyncing = true;
      AutoCacheService.progress = {
        total: 1,
        completed: 0,
        sections: {},
      };
      
      // Simulate a sync process
      setTimeout(() => {
        AutoCacheService.progress.completed = 1;
        AutoCacheService.isSyncing = false;
      }, 3000);
      
      return true;
    };
  }
  
  // Ensure getProgress method exists
  if (!AutoCacheService.getProgress) {
    AutoCacheService.getProgress = () => {
      return {
        ...AutoCacheService.progress,
        isComplete: AutoCacheService.progress.completed >= AutoCacheService.progress.total,
        isSyncing: AutoCacheService.isSyncing,
      };
    };
  }
  
  // Add basic implementation for checkForUpdates if missing
  if (!AutoCacheService.checkForUpdates) {
    AutoCacheService.checkForUpdates = async () => {
      if (!navigator.onLine) {
        return { status: 'offline', updates: [] };
      }
      
      return { status: 'complete', updates: [] };
    };
  }
  
  // Add basic implementation for applyUpdates if missing
  if (!AutoCacheService.applyUpdates) {
    AutoCacheService.applyUpdates = async (updates) => {
      if (!updates || updates.length === 0) {
        return { status: 'no-updates' };
      }
      
      return { status: 'complete', results: { new: 0, updated: 0, deleted: 0, failed: 0 } };
    };
  }
};

// Ensure these functions are implemented
const patchBandwidthLimitedDownloadService = () => {
  console.log('[Compatibility] Patching BandwidthLimitedDownloadService');
  
  // Define constants if they don't exist
  if (!BandwidthLimitedDownloadService.STORAGE_KEY_USAGE) {
    BandwidthLimitedDownloadService.STORAGE_KEY_USAGE = 'bandwidth_usage';
  }
  
  if (!BandwidthLimitedDownloadService.STORAGE_KEY_DATE) {
    BandwidthLimitedDownloadService.STORAGE_KEY_DATE = 'bandwidth_date';
  }
  
  if (!BandwidthLimitedDownloadService.DAILY_BACKGROUND_LIMIT) {
    BandwidthLimitedDownloadService.DAILY_BACKGROUND_LIMIT = 1 * 1024 * 1024; // 1MB in bytes
  }
  
  // Ensure the initialize method exists
  if (!BandwidthLimitedDownloadService.initialize) {
    BandwidthLimitedDownloadService.initialize = () => {
      console.log('[Compatibility] Called BandwidthLimitedDownloadService.initialize');
      
      // Reset daily tracker if it's a new day
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const lastTrackedDay = localStorage.getItem(BandwidthLimitedDownloadService.STORAGE_KEY_DATE);
      
      if (lastTrackedDay !== today) {
        localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE, '0');
        localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_DATE, today);
      }
    };
  }
  
  // Add getCurrentUsage method if missing
  if (!BandwidthLimitedDownloadService.getCurrentUsage) {
    BandwidthLimitedDownloadService.getCurrentUsage = () => {
      return parseInt(localStorage.getItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE) || '0');
    };
  }
  
  // Add trackUsage method if missing
  if (!BandwidthLimitedDownloadService.trackUsage) {
    BandwidthLimitedDownloadService.trackUsage = (bytes) => {
      const currentUsage = BandwidthLimitedDownloadService.getCurrentUsage();
      const newUsage = currentUsage + bytes;
      localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE, newUsage.toString());
      console.log(`[BandwidthLimitedDownloadService] Updated usage: ${newUsage} bytes (${(newUsage / (1024 * 1024)).toFixed(2)} MB)`);
    };
  }
  
  // Add canDownloadInBackground method if missing
  if (!BandwidthLimitedDownloadService.canDownloadInBackground) {
    BandwidthLimitedDownloadService.canDownloadInBackground = (requiredBytes) => {
      const currentUsage = BandwidthLimitedDownloadService.getCurrentUsage();
      return (currentUsage + requiredBytes) <= BandwidthLimitedDownloadService.DAILY_BACKGROUND_LIMIT;
    };
  }
  
  // Add queueForDownload method if missing
  if (!BandwidthLimitedDownloadService.queueForDownload) {
    BandwidthLimitedDownloadService.downloadQueue = [];
    BandwidthLimitedDownloadService.isProcessingQueue = false;
    
    BandwidthLimitedDownloadService.queueForDownload = (document) => {
      // Check if auto-caching is disabled
      const autoCaching = localStorage.getItem('autoCaching');
      if (autoCaching === 'false') {
        return;
      }
      
      // Add to queue
      BandwidthLimitedDownloadService.downloadQueue.push(document);
      console.log(`[BandwidthLimitedDownloadService] Added to queue: ${document.name}`);
    };
  }
  
  // Add clearQueue method if missing
  if (!BandwidthLimitedDownloadService.clearQueue) {
    BandwidthLimitedDownloadService.clearQueue = () => {
      BandwidthLimitedDownloadService.downloadQueue = [];
      console.log('[BandwidthLimitedDownloadService] Download queue cleared');
    };
  }
  
  // Add processDownloadQueue method if missing
  if (!BandwidthLimitedDownloadService.processDownloadQueue) {
    BandwidthLimitedDownloadService.processDownloadQueue = async (deadline) => {
      // If no deadline provided (for manual testing), create a stub
      deadline = deadline || { timeRemaining: () => 5000, didTimeout: false };
      
      // Check if auto-caching is disabled
      const autoCaching = localStorage.getItem('autoCaching');
      if (autoCaching === 'false') {
        BandwidthLimitedDownloadService.downloadQueue = [];
        return;
      }
      
      // Mark as processing
      BandwidthLimitedDownloadService.isProcessingQueue = true;
      
      try {
        // Process queue until we hit time or bandwidth limit
        while (
          BandwidthLimitedDownloadService.downloadQueue.length > 0 && 
          (deadline.timeRemaining() > 100 || deadline.didTimeout)
        ) {
          const nextDoc = BandwidthLimitedDownloadService.downloadQueue[0];
          
          try {
            // Check if document is already cached
            const isCached = await DocumentService.isDocumentCached(nextDoc.id);
            if (isCached) {
              // Already cached, remove from queue
              BandwidthLimitedDownloadService.downloadQueue.shift();
              continue;
            }
            
            // Process one document
            await BandwidthLimitedDownloadService.downloadDocument(nextDoc);
          } catch (error) {
            console.error(`[BandwidthLimitedDownloadService] Error processing queued document:`, error);
          }
          
          // Remove from queue
          BandwidthLimitedDownloadService.downloadQueue.shift();
        }
      } finally {
        // Mark as no longer processing
        BandwidthLimitedDownloadService.isProcessingQueue = false;
        
        // Schedule next run if there are still items in the queue
        if (
          BandwidthLimitedDownloadService.downloadQueue.length > 0 && 
          'requestIdleCallback' in window
        ) {
          window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
        }
      }
    };
  }
  
  // Add queueSectionDocuments method if missing
  if (!BandwidthLimitedDownloadService.queueSectionDocuments) {
    BandwidthLimitedDownloadService.queueSectionDocuments = (sectionPath, documents) => {
      // Check if auto-caching is disabled
      const autoCaching = localStorage.getItem('autoCaching');
      if (autoCaching === 'false') {
        return;
      }
      
      if (!documents || documents.length === 0) return;
      
      console.log(`[BandwidthLimitedDownloadService] Queueing ${documents.length} documents from ${sectionPath}`);
      
      // Queue each document
      documents.forEach(doc => {
        BandwidthLimitedDownloadService.queueForDownload(doc);
      });
    };
  }
  
  // Ensure downloadDocument with better error handling
  if (BandwidthLimitedDownloadService.downloadDocument) {
    const originalDownloadDocument = BandwidthLimitedDownloadService.downloadDocument;
    
    BandwidthLimitedDownloadService.downloadDocument = async (document) => {
      // First check if it's already cached
      try {
        const isCached = await DocumentService.isDocumentCached(document.id);
        if (isCached) {
          console.log(`[BandwidthLimitedDownloadService] Document already cached: ${document.name}`);
          return true;
        }
      } catch (error) {
        console.error(`[BandwidthLimitedDownloadService] Error checking cache: ${error.message}`);
      }
      
      // Check network status before attempting download
      let isActuallyOnline = navigator.onLine;
      
      if (isActuallyOnline && window.checkNetworkConnectivity) {
        isActuallyOnline = await window.checkNetworkConnectivity();
      }
      
      if (!isActuallyOnline) {
        throw new Error('Device is offline, cannot download document');
      }
      
      try {
        return await originalDownloadDocument(document);
      } catch (error) {
        console.error(`[BandwidthLimitedDownloadService] Error downloading: ${error.message}`);
        throw error;
      }
    };
  } else {
    // Create a basic implementation if missing
    BandwidthLimitedDownloadService.downloadDocument = async (document) => {
      try {
        console.log(`[BandwidthLimitedDownloadService] User-initiated download: ${document.name}`);
        
        // Check if already cached
        const isCached = await DocumentService.isDocumentCached(document.id);
        console.log(`[BandwidthLimitedDownloadService] Document cached check: ${isCached}`);
        
        if (isCached) {
          console.log(`[BandwidthLimitedDownloadService] Document already cached: ${document.name}`);
          return true;
        }
        
        const fileRef = ref(storage, document.path);
        const url = await getDownloadURL(fileRef);
        
        // Fetch the document
        const response = await fetch(url, { 
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/octet-stream'
          },
          credentials: 'same-origin'
        });
        
        // Get the binary data
        const blob = await response.blob();
        console.log(`[BandwidthLimitedDownloadService] Downloaded blob size: ${blob.size}`);
        
        // Store in IndexedDB
        await DocumentService.storeBlobInIndexedDB(document.id, blob);
        
        // Update document metadata
        await DocumentService.updateDocumentCacheStatus(document.id);
        
        // Track bandwidth (for analytics only, not limited)
        const fileSize = blob.size || 0;
        if (BandwidthLimitedDownloadService.trackUsage) {
          BandwidthLimitedDownloadService.trackUsage(fileSize);
        }
        
        console.log(`[BandwidthLimitedDownloadService] Download completed: ${document.name}`);
        return true;
      } catch (error) {
        console.error(`[BandwidthLimitedDownloadService] Error in download: ${document.path}`, error);
        return false;
      }
    };
  }
  
  // Ensure startBackgroundCaching method exists
  if (!BandwidthLimitedDownloadService.startBackgroundCaching) {
    BandwidthLimitedDownloadService.startBackgroundCaching = async () => {
      const autoCaching = localStorage.getItem('autoCaching');
      if (autoCaching !== 'true') {
        console.log('[BandwidthLimitedDownloadService] Auto-caching is disabled.');
        return;
      }
      
      console.log('[BandwidthLimitedDownloadService] Starting background caching.');
      
      // Start download queue processing
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
      }
    };
  }
  
  // Ensure the download queue processing is properly set up
  if (BandwidthLimitedDownloadService.processDownloadQueue && 
      !BandwidthLimitedDownloadService.isProcessingQueue &&
      'requestIdleCallback' in window) {
    window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
  }
};

// Export helpers for programmatic access
const compatibilityHelpers = {
  isActuallyOnline: async () => {
    if (window.checkNetworkConnectivity) {
      return await window.checkNetworkConnectivity();
    }
    return navigator.onLine;
  },
  
  getNetworkStatus: () => {
    return window.networkStatus || { isActuallyOnline: navigator.onLine, lastChecked: Date.now() };
  },
  
  forceOfflineMode: () => {
    if (window.networkStatus) {
      window.networkStatus.isActuallyOnline = false;
      window.networkStatus.lastChecked = Date.now();
    }
    // Also update navigator.onLine (this is just for testing, doesn't affect real status)
    Object.defineProperty(navigator, 'onLine', { value: false, configurable: true });
  },
  
  resetNetworkStatus: () => {
    if (window.networkStatus) {
      window.networkStatus.isActuallyOnline = navigator.onLine;
      window.networkStatus.lastChecked = Date.now();
    }
  },
  
  // Force a sync with the server
  forceSyncWithServer: async () => {
    if (DocumentService.syncWithServer) {
      return await DocumentService.syncWithServer();
    }
    return false;
  },
  
  // Clear all cached data for debugging/testing
  clearAllCachedData: async () => {
    if (DocumentService.clearAllCachedData) {
      return await DocumentService.clearAllCachedData();
    } else {
      try {
        const db = await openDB('trinityHarperDocs', 1);
        // Clear all object stores
        const docsTx = db.transaction('documents', 'readwrite');
        await docsTx.objectStore('documents').clear();
        
        const contentTx = db.transaction('documentContents', 'readwrite');
        await contentTx.objectStore('documentContents').clear();
        
        const syncTx = db.transaction('syncStatus', 'readwrite');
        await syncTx.objectStore('syncStatus').clear();
        
        await docsTx.done;
        await contentTx.done;
        await syncTx.done;
        
        console.log('[Compatibility] All cached data cleared successfully');
        return true;
      } catch (error) {
        console.error('[Compatibility] Error clearing cached data:', error);
        return false;
      }
    }
  },
  
  // Check database status
  checkDatabaseStatus: async () => {
    try {
      const db = await openDB('trinityHarperDocs', 1);
      
      // Get document count
      const docsTx = db.transaction('documents', 'readonly');
      const docsStore = docsTx.objectStore('documents');
      const docCount = await docsStore.count();
      
      // Get content count
      const contentTx = db.transaction('documentContents', 'readonly');
      const contentStore = contentTx.objectStore('documentContents');
      const contentCount = await contentStore.count();
      
      // Get sync status count
      const syncTx = db.transaction('syncStatus', 'readonly');
      const syncStore = syncTx.objectStore('syncStatus');
      const syncCount = await syncStore.count();
      
      return {
        status: 'ok',
        documentCount: docCount,
        contentCount: contentCount,
        syncCount: syncCount
      };
    } catch (error) {
      return {
        status: 'error',
        error: error.message
      };
    }
  }
};

export { initializeCompatibilityLayer };
